import {
  CALENDAR_HEIGHT_COMFORTABLE,
  CALENDAR_HEIGHT_COMPACT,
  CALENDAR_HEIGHT_DEFAULT,
  CalendarDensity,
  CalendarTimeDial,
} from "@clockwise/web-commons/src/components/calendar";
import { useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { useReadCalendar } from "@clockwise/web-commons/src/util/CalendarContext";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames";
import { isEmpty, isEqual } from "lodash";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReadLocalStorage } from "usehooks-ts";
import { useUpdateActiveEventDiff } from "../../chat-plus-calendar/util/ActiveDiffContext";
import { RescheduleConfirmationWrapper } from "../../reschedule-confirmation-modal";
import { usePlannerContext } from "../Context";
import { CalendarScrollSync } from "../calendar-week/CalendarScrollSync";
import { useAvailablities } from "../hooks/useAvailablities";
import DateTimeMarker from "../planner-datetime-marker/DateTimeMarker";
import { SingleDayHead } from "../single-day-head/SingleDayHead";
import { TimeSuggestion } from "../time-suggestion";
import {
  useReadHorizontalScrollSync,
  useUpdateHorizontalScrollSync,
} from "./HorizonalScrollSyncContext";
import { SingleDayEventsColumn } from "./SingleDayEventsColumn";

type Next = (_: Record<string, string | number | boolean>) => Promise<unknown>;

const MIN_COLUMN_WIDTH_PERCENTAGE = 18;
export const CalendarSingleDay = () => {
  const { eventsByDayAndCalendar, primaryCalendarId, multiCalendarIds } = usePlannerContext();

  const calendarState = useReadCalendar();
  const updateActiveEvent = useUpdateActiveEvent();
  const updateActiveEventDiff = useUpdateActiveEventDiff();

  const [dateToRender, setDateToRender] = useState<string>(calendarState.visibleDates[0]);
  const zone = getRenderTimeZone();
  const dateTimeToRender = useMemo(() => DateTime.fromISO(dateToRender, { zone }), [
    dateToRender,
    zone,
  ]);

  const {
    intervalsByCalendarId: availabilities,
    timeZoneByCalendarId: timeZones,
  } = useAvailablities({
    dateTimes: [dateTimeToRender],
    minCalendarCount: 2,
  });

  const [animateCalStyles, animateCal] = useSpring(() => ({}));

  const animateCalendar = useCallback(
    (goForward: boolean, goToNewDate: () => void) => {
      const distance = 16, // React spring seems to do best with an even number here.
        duration = 135;
      animateCal.start(() => ({
        to: async (next: Next) => {
          await next({ translateX: -distance * (goForward ? 1 : -1), opacity: 0.25 });
          await next({
            translateX: distance * 1.8 * (goForward ? 1 : -1),
            opacity: 0,
            immediate: true,
          });
          await next({ immediate: true, opacity: 0.5 });
          goToNewDate();
          await next({ translateX: 0, opacity: 1 });
        },
        config: { duration },
      }));
    },
    [animateCal],
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const { calendarRef: calendarHorizonalScrollRef } = useReadHorizontalScrollSync();
  const { handleScrollCalendar } = useUpdateHorizontalScrollSync();

  useEffect(() => {
    // if the render dates are the same, don't animate
    if (isEqual(dateToRender, calendarState.visibleDates)) {
      return;
    }

    // determine if we should animate forward or backward
    const newFirstRenderDateTime = DateTime.fromISO(calendarState.visibleDates[0]);
    const stepForward = newFirstRenderDateTime > dateTimeToRender;

    // animate the calendar, and update local state
    animateCalendar(stepForward, () => {
      setDateToRender(calendarState.visibleDates[0]);
    });
  }, [calendarState.visibleDates]);

  const calendarDensity = useReadLocalStorage<CalendarDensity>("calendarDensity");

  let calHeight = CALENDAR_HEIGHT_DEFAULT;
  if (calendarDensity === "compact") {
    calHeight = CALENDAR_HEIGHT_COMPACT;
  } else if (calendarDensity === "comfortable") {
    calHeight = CALENDAR_HEIGHT_COMFORTABLE;
  }

  const eventsByCalendarForDay = useMemo(() => {
    return eventsByDayAndCalendar ? eventsByDayAndCalendar[dateToRender] : null;
  }, [dateToRender, eventsByDayAndCalendar]);

  const allDayEventsByCalendar = eventsByCalendarForDay?.allDayEvents ?? {};

  const calendarIds = primaryCalendarId ? [primaryCalendarId].concat(multiCalendarIds ?? []) : [];

  const handleSelectEvent = (calendarId: string, externalEventId: string) => {
    updateActiveEventDiff(null);
    updateActiveEvent({ calendarId, externalEventId });
  };

  const showWorkingHoursHash = !isEmpty(availabilities);

  return (
    <animated.div
      style={animateCalStyles}
      className="cw-flex cw-flex-1 cw-flex-col cw-mt-6 cw-w-[calc(100%-12px)] cw-h-full"
    >
      <div className="cw-relative cw-flex-1 cw-w-full cw-h-full cw-flex cw-flex-col cw-my-0 cw-mx-3 cw-pb-3">
        <div className="cw-border-b cw-border-subtle cw-border-solid cw-w-full">
          <SingleDayHead
            dateTime={dateTimeToRender}
            calendarIds={calendarIds}
            onEventClick={handleSelectEvent}
            timeZones={timeZones}
            minWidthPercent={MIN_COLUMN_WIDTH_PERCENTAGE}
            allDayEventsByCalendar={allDayEventsByCalendar}
          />
        </div>
        <div className="cw-overflow-auto cw-mb-[66px] cw-flex-grow" ref={scrollContainerRef}>
          <div
            style={{ minHeight: calHeight }}
            className="cw-relative cw-flex cw-flex-row cw-flex-1 cw-h-full cw-w-full"
            ref={calendarContainerRef}
          >
            {/* Non-rendering component that adjusts the scrollTop based on:
                1. On first mount, any previous scroll events initiated by the user
                2. The current time on routing to /app/ai-scheduler (via extension)
                3. The earliest start time in a conversation (proposal or viewEvents)
             */}
            <CalendarScrollSync
              scrollContainerRef={scrollContainerRef}
              calendarContainerRef={calendarContainerRef}
            />
            <div className="cw-flex cw-sticky">
              <CalendarTimeDial />
            </div>
            <DateTimeMarker daysRendered={[dateTimeToRender]} />
            <div
              className={classNames(
                "cw-flex cw-flex-row cw-justify-center cw-flex-1 cw-flex-grow cw-relative cw-h-[calc(100%+1px)] cw-w-full",
                { "cw-bg-subtle-striped": showWorkingHoursHash },
                "cw-ml-5 cw-overflow-y-hidden cw-overflow-x-scroll",
              )}
              ref={calendarHorizonalScrollRef}
              onScroll={handleScrollCalendar}
            >
              {calendarIds.map((calendarId, index) => {
                return (
                  <div key={`${calendarId}-event-column`}>
                    <SingleDayEventsColumn
                      calendarId={calendarId}
                      dateTime={dateTimeToRender}
                      columnIndex={index}
                      columnCount={calendarIds.length}
                      availability={availabilities[calendarId] ?? null}
                      minWidthPercent={MIN_COLUMN_WIDTH_PERCENTAGE}
                    />
                  </div>
                );
              })}
              {/* for now time suggestions will always be shown on your calendar */}
              <TimeSuggestion
                dateTimes={[dateTimeToRender]}
                columnIndex={0}
                columnCount={calendarIds.length}
              />
            </div>
            <RescheduleConfirmationWrapper />
          </div>
        </div>
      </div>
    </animated.div>
  );
};
